import React, {useEffect, useCallback} from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Navigation from '../components/Navigation/Navigation';
import {useSelector} from 'react-redux';
import {useOktaAuth} from '@okta/okta-react';
import useSiteInfo from 'hooks/useSiteInfo/useSiteInfo';
import {useSiteState, siteActionTypes} from 'hooks/useSiteInfo/SiteStateProvider';
import {SiteResponse} from 'hooks/useSiteInfo/interfaces/Site';
import {useUserInfoState, userInfoActionTypes} from 'hooks/useUserInfo/UserInfoProvider';

const StyledLayout = styled.div`
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  padding-bottom: 64px;
  position: relative;
  /* margin-left needs to match the width the the side nav */
  margin-left: 0px;
  transition: 0.25s;
  padding-top: 228px;
  @media (min-width: 600px) {
    margin-left: 70px;
    padding-top: 0px;
    &.menu-expanded {
      margin-left: 210px;
    }
  }

  /* padding-top needs to match the header height */
  /* padding-top: 62px; */
`;

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
  match?: any;
  breadCrumb?: any;
  headerContent?: React.ReactNode;
  disableMaxWidth?: boolean;
  compactHeader?: boolean;
}

function Layout({
  children,
  className,
  match,
  breadCrumb,
  headerContent,
  disableMaxWidth,
  compactHeader,
}: LayoutProps) {
  //#region Okta Auth
  const {oktaAuth} = useOktaAuth();
  const {
    state: {userInfo, debugMode},
    dispatch: userInfoDispatch,
  } = useUserInfoState();

  useEffect(() => {
    const dispatchSetUserInfo = async () => {
      const user = await oktaAuth.getUser();
      userInfoDispatch({type: userInfoActionTypes.UPDATE_USER_INFO, payload: user});
    };
    if (userInfo === null) {
      dispatchSetUserInfo();
    }
  }, [oktaAuth, userInfoDispatch, userInfo]);
  //#endregion Okta Auth

  const expand = useSelector((state: {navigation: boolean}) => state.navigation);

  //#region Site State
  const {
    state: {isSiteStateReady, isTestSitesEnabled},
    dispatch: siteDispatch,
  } = useSiteState();

  const dispatchUpdateSites = useCallback(
    (siteData: SiteResponse[]) => {
      siteDispatch({
        type: siteActionTypes.UPDATE_SITES,
        payload: siteData,
      });
    },
    [siteDispatch]
  );

  const {siteInfoData, pending} = useSiteInfo();

  useEffect(() => {
    if (!pending && siteInfoData !== null && !isSiteStateReady) {
      dispatchUpdateSites(siteInfoData!);
    }
  }, [siteInfoData, pending, dispatchUpdateSites, isSiteStateReady]);

  useEffect(() => {
    if (debugMode && !isTestSitesEnabled) {
      siteDispatch({
        type: siteActionTypes.ENABLE_TEST_SITES,
      });
    } else if (!debugMode && isTestSitesEnabled) {
      siteDispatch({
        type: siteActionTypes.DISABLE_TEST_SITES,
      });
    }
  }, [debugMode, siteDispatch, isTestSitesEnabled]);
  //#endregion Site State

  return (
    <StyledLayout
      id={'qc-layout-container'}
      className={(expand ? 'menu-expanded ' : '') + className}
    >
      <Navigation />
      <Header
        breadCrumb={breadCrumb}
        match={match}
        headerContent={headerContent}
        disableMaxWidth={disableMaxWidth}
        compact={compactHeader}
      />

      {children}
    </StyledLayout>
  );
}

export default Layout;
